import { createContext, useContext, useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useApolloClient } from '@apollo/client';
import { Card, CardContent } from '@/components/ui/card';
import type { UnifiedTableColumn } from '@/components/ui/unifiedTable/UnifiedTable';
import { policyMatches } from '@/lib/policies';
import { useUserPermissions } from '@/store/useUserPermissions';

export type LegacyProgram = 'mist' | 'fist' | 'odrhousing' | 'lead' | 'prg_wmn' | 'dsh';
export const legacyPrograms: { key: LegacyProgram; label: string }[] = [
  { key: 'mist', label: 'MIST' }, { key: 'fist', label: 'FIST' },
  { key: 'odrhousing', label: 'ODR Housing' }, { key: 'lead', label: 'LEAD' },
  { key: 'prg_wmn', label: 'PRG_WMN' }, { key: 'dsh', label: 'DSH' },
];
export type LegacyProfileProps = { program: LegacyProgram; participant: string; refreshProfile: () => void };
export type LegacyRow = Record<string, string | null | undefined>;

/** Decode one HTML-entity layer without interpreting any stored HTML markup. */
export const legacyText = (value: string | null | undefined): string => {
  const text = value ?? '';
  if (typeof document === 'undefined' || !text.includes('&')) return text;
  const decoder = document.createElement('textarea');
  return text.replace(/&(?:#[0-9]+|#x[0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]+);/g, (entity) => {
    // Only a single entity token, never a tag or user-provided HTML fragment.
    decoder.innerHTML = entity;
    return decoder.value;
  });
};

export const useLegacyAccess = () => {
  const { policies } = useUserPermissions();
  return (required: string) => (policies as string[]).some((policy) => policyMatches(policy, required));
};

export const LegacySection = ({ title, children, loading, error, id }: {
  title: string; children?: ReactNode; loading?: boolean; error?: { message: string } | null; id: string;
}) => (
  <section id={id} aria-labelledby={`${id}-title`} className="legacy-section">
    <h2 id={`${id}-title`} className="legacy-section__title">{title}</h2>
    {loading && <p role="status" className="legacy-state-message">Loading…</p>}
    {error && <p role="alert" className="legacy-state-message legacy-state-message--error">{error.message}</p>}
    {!loading && !error && children}
  </section>
);

export const LegacyEditor = ({ title, children }: { title: string; children: ReactNode }) => (
  <Card className="legacy-editor">
    <CardContent className="legacy-editor__content">
      <h3 className="legacy-editor__title">{title}</h3>
      {children}
    </CardContent>
  </Card>
);

const LegacyPopupDepth = createContext(0);

/** Emulates a legacy link that navigated away while retaining the profile underneath. */
export const LegacyFullPageEditor = ({ title, children, onClose }: { title: string; children: ReactNode; onClose: () => void }) => {
  const titleId = useId();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const depth = useContext(LegacyPopupDepth);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    titleRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      const openPopups = Array.from(document.querySelectorAll<HTMLElement>('[data-legacy-popup-depth]'));
      const topPopup = openPopups.reduce<HTMLElement | null>((top, popup) => {
        if (!top) return popup;
        return Number(popup.dataset.legacyPopupDepth) >= Number(top.dataset.legacyPopupDepth) ? popup : top;
      }, null);
      if (topPopup === popupRef.current) closeRef.current();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [titleId]);

  return createPortal(
    <div ref={popupRef} className="legacy-emulator legacy-full-page-popup" role="dialog" aria-modal="true" aria-labelledby={titleId} data-legacy-popup-depth={depth} style={{ zIndex: 40 + depth }}>
      <div className="legacy-full-page-popup__page">
        <div className="legacy-full-page-popup__tools">
          <div>
            <span className="legacy-full-page-popup__eyebrow">Participant workspace</span>
            <h1 id={titleId} ref={titleRef} tabIndex={-1}>{title}</h1>
          </div>
          <button type="button" className="legacy-full-page-popup__close" onClick={onClose}>Close</button>
        </div>
        <div className="legacy-full-page-popup__content"><LegacyPopupDepth.Provider value={depth + 1}>{children}</LegacyPopupDepth.Provider></div>
      </div>
    </div>,
    document.body,
  );
};

export const LegacyDataTable = <T extends object,>({ rows, columns, actions, actionsHeader, rowKey, rowClassName, empty = 'No records.' }: {
  rows: T[]; columns: UnifiedTableColumn<T>[]; actions?: (row: T, index: number) => ReactNode;
  actionsHeader?: ReactNode; rowKey?: (row: T, index: number) => string; rowClassName?: (row: T) => string; empty?: string | null;
}) => <div className="legacy-data-table">
  <table cellPadding="5" cellSpacing="0">
    <thead><tr>
      {columns.map((column) => <th key={column.key}>{column.header}</th>)}
      {actions && <th aria-label="Actions">{actionsHeader}</th>}
    </tr></thead>
    <tbody>
      {!rows.length && empty !== null && <tr><td colSpan={columns.length + (actions ? 1 : 0)}>{empty}</td></tr>}
      {rows.map((row, index) => <tr key={rowKey ? rowKey(row, index) : String(index)} className={rowClassName?.(row)}>
        {columns.map((column) => <td key={column.key}>{column.accessor ? column.accessor(row) : column.field ? String(row[column.field] ?? '') : ''}</td>)}
        {actions && <td className="legacy-data-table__actions">{actions(row, index)}</td>}
      </tr>)}
    </tbody>
  </table>
</div>;

export const legacyDate = (value: string | null | undefined, long = false) => {
  if (!value) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return value;
  if (long) return `${match[2]}/${match[3]}/${match[1]}`;
  return `${Number(match[2])}/${Number(match[3])}/${match[1].slice(2)}`;
};

export const downloadLegacyFile = (file: { content?: string | null; contentType?: string | null; filename?: string | null }, preview?: Window) => {
  if (!file.content) throw new Error('The file is empty or unavailable.');
  const decoded = window.atob(file.content);
  const bytes = Uint8Array.from(decoded, (character) => character.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: file.contentType || 'application/pdf' }));
  if (preview) {
    preview.location.href = url;
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return;
  }
  const link = document.createElement('a');
  link.href = url;
  link.download = file.filename || 'document.pdf';
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

export const readLegacyFile = (file: File): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('Unable to read the selected file.'));
  reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
  reader.readAsDataURL(file);
});

const profileQueries = [
  'legacyShowFullMain', 'legacyShowFullLegal', 'legacyShowFullMedical', 'legacyShowFullLocation',
  'legacyShowFullNotifications', 'legacyShowFullCoordinatedCare', 'legacyShowFullNotes',
  'legacyShowFullIncidents', 'legacyShowFullEvents', 'legacyShowFullInsurance',
  'legacyShowFullEnrollment', 'legacyShowFullDemographics', 'legacyShowFullScans',
];

/** Inactive sections are invalidated without fetching them. Mounted sections refresh. */
export const useLegacyRefresh = () => {
  const client = useApolloClient();
  return () => {
    void client.refetchQueries({
      updateCache(cache) {
        for (const fieldName of profileQueries) cache.evict({ id: 'ROOT_QUERY', fieldName });
      },
    });
  };
};
